import React, { useCallback, useEffect, useState } from "react";
import { Box, Card, CardContent, CardHeader, TableFooter } from "@mui/material";
import { map } from "ramda";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import EditIcon from "@mui/icons-material/Edit";
import Typography from "@mui/material/Typography";
import YesNoCard from "../../components/YesNoCard";
import { useUsers } from "../../user-admin/userServices";
import TablePagination from "@mui/material/TablePagination";
import { Link, useNavigate, useLocation } from "react-router-dom";
import CreateUserDialog from "../../user-admin/CreateUserDialog";
import Button from "@mui/material/Button";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },

  cell: {
    minWidth: 125,
  },
}));

const OrganizationUsersCard = ({ id, allowAdd }) => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const userResponse = useUsers([], [], id, "loginCount:desc", true, page, 4);

  useEffect(() => {
    if (userResponse.isSuccess && notBlank(userResponse.data)) {
      setUsers(userResponse.data.contents);
      setTotal(userResponse.data.count);
    } else {
      setUsers([]);
      setTotal(0);
    }
  }, [userResponse.isSuccess, userResponse.data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleUserCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleUserSave = useCallback(
    async data => {
      await userResponse.refetch();
      setDialogOpen(false);
      navigate(`/user-admin/users/${data}`, {
        state: {
          previousLocation: location.pathname,
        },
      });
    },
    [userResponse, navigate, location.pathname],
  );

  const handlePageChange = useCallback(page => setPage(page), []);

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Active Users"
        action={
          allowAdd && (
            <Tooltip title="Add User">
              <IconButton onClick={handleAddClick} size="large">
                <AddIcon color="primary" fontSize="large" />
              </IconButton>
            </Tooltip>
          )
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {userResponse.isError && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            <Typography color="error">
              Error loading Organization Users.
            </Typography>
          </Box>
        )}
        {userResponse.isLoading && <PanelLoadingIndicator />}
        {userResponse.isSuccess && isBlank(users) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Active Users associated to this Organization.
          </Box>
        )}
        {userResponse.isSuccess && notBlank(users) && (
          <TableContainer>
            <Table stickyHeader aria-label="Active Users">
              <TableHead>
                <TableRow>
                  <TableCell
                    key="accountName"
                    align="left"
                    className={classes.contentCell}
                  >
                    Username
                  </TableCell>

                  <TableCell
                    key="emailAddress"
                    align="left"
                    className={classes.cell}
                  >
                    Email Address
                  </TableCell>

                  <TableCell
                    key="loginCount"
                    align="right"
                    className={classes.cell}
                  >
                    Login Count
                  </TableCell>

                  <TableCell
                    key="email-address-verified"
                    align="center"
                    className={classes.cell}
                  >
                    Verified Email
                  </TableCell>

                  <TableCell
                    key="actions"
                    align="center"
                    style={{ width: 50 }}
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {map(
                  user => (
                    <TableRow hover tabIndex={-1} key={user.userId}>
                      <TableCell
                        key={"userId:" + user.userId}
                        align="left"
                        className={classes.contentCell}
                      >
                        {user.username}
                      </TableCell>

                      <TableCell
                        key={"email-address:" + user.userId}
                        align="left"
                      >
                        {user.emailAddress}
                      </TableCell>

                      <TableCell
                        key={"login-count:" + user.userId}
                        align="right"
                        className={classes.contentTypeCell}
                      >
                        {user.loginCount}
                      </TableCell>

                      <TableCell
                        key={"email-address-verified:" + user.userId}
                        align="center"
                      >
                        <YesNoCard
                          value={user.emailAddressVerified}
                          showNoIcon={false}
                        />
                      </TableCell>

                      <TableCell
                        key={"actions:" + user.userId}
                        align="center"
                        style={{ width: 50 }}
                      >
                        <Box display="flex" justifyContent="center" pl={4}>
                          <Tooltip title="Edit">
                            <IconButton
                              aria-label="edit user"
                              classes={{ label: classes.icon }}
                              size="large"
                            >
                              <Link
                                to={{
                                  pathname: `/user-admin/users/${user.userId}`,
                                }}
                                state={{
                                  previousLocation: location.pathname,
                                }}
                              >
                                <EditIcon color="primary" fontSize="large" />
                              </Link>
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ),
                  users,
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={2}>
                    <Button
                      variant="text"
                      onClick={() =>
                        navigate(`/user-admin/users`, {
                          state: {
                            organizationId: id,
                            previousLocation: location.pathname,
                          },
                        })
                      }
                    >
                      All Users
                    </Button>
                  </TableCell>

                  <TablePagination
                    classes={{
                      root: classes.pagination,
                    }}
                    rowsPerPageOptions={[4]}
                    colSpan={5}
                    count={total}
                    rowsPerPage={4}
                    page={page}
                    onPageChange={(event, page) => handlePageChange(page)}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      {dialogOpen && (
        <CreateUserDialog
          open={dialogOpen}
          organizationId={id}
          onCancel={handleUserCancel}
          onSave={handleUserSave}
        />
      )}
    </Card>
  );
};

export default OrganizationUsersCard;
